import React, { useMemo, useRef } from "react";
import { PostFieldLabel } from "components/Posts/PostForm";
import PostFieldError from "components/Posts/PostForm/PostFieldError/PostFieldError";
import { FIELDS } from "constants/PostFormFields";
import { useSkedCaptionAutocomplete } from "ui/SkedCaption";
import {
  DEFAULT_HASHTAG_LIMIT,
  getStrictestRuleMax,
  postPlatformsConfig,
  RULES,
} from "../postPlatforms.config";
import { useDebouncedCallback } from "use-debounce";
import { SkedEditorHistory } from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import { SkedCaptionV2Lazy as SkedCaptionV2 } from "../../../../ui/SkedCaption/v2/sked-caption-v2.lazy";
import { useSelector } from "react-redux";
import { getDescriptions } from "../../../../selectors/postFormSelectors";
import { PlatformSpec } from "../../../../constants/Platforms";
import {
  FormValidationErrors,
  FormValidationWarnings,
} from "../../../../shared/types/post";
import {
  AccountPlatformType,
  PostLinkedInAccountType,
} from "../../../../shared/types/accounts";
import {
  LexicalEditor,
  $getRoot,
  HISTORY_MERGE_TAG,
  HISTORY_PUSH_TAG,
  UNDO_COMMAND,
} from "lexical";
import { skedCaptionImport } from "../../../../ui/SkedCaption/v2/sked-caption-v2";
import { InlineAiCaptionPopover } from "./inline-ai-caption-popover";
import type { Label } from "../../../../entities/posts/components/templates/post-form-base-template";
import type { ReduxRootState } from "../../../../types/redux-store";
import { countMentionsForPlatform } from "../utils/mentionCount";
import { useRegisterFlush } from "../platform-fields/flush-registry";

const getPlatformMentionLimit = (
  platformType?: AccountPlatformType | "LINKED"
) => {
  if (!platformType || platformType === "LINKED") {
    return;
  }
  return (postPlatformsConfig?.[platformType] as any)?.validation?.error?.[
    FIELDS.CAPTION
  ]?.[RULES.MENTION_COUNT]?.[1];
};

const getMostRestrictiveMentionLimit = (platforms: PlatformSpec[]) => {
  let mostRestrictiveMentionLimit: number | undefined;

  for (const { TYPE } of platforms) {
    const platformMentionLimit = getPlatformMentionLimit(TYPE);

    if (platformMentionLimit === undefined) {
      continue;
    }

    if (
      mostRestrictiveMentionLimit === undefined ||
      mostRestrictiveMentionLimit > platformMentionLimit
    ) {
      mostRestrictiveMentionLimit = platformMentionLimit;
    }
  }

  return mostRestrictiveMentionLimit;
};

interface Props {
  platforms?: PlatformSpec[];
  selectedPlatform: AccountPlatformType | "LINKED";
  value: string;
  characterCount?: number;
  className?: string;
  label: React.ReactNode;
  disabled?: boolean;
  onChange: (value: string) => void;
  maxLength: number;
  hashtagCount?: number;
  errors?: FormValidationErrors;
  warnings?: FormValidationWarnings;
  hasMoreThanOnelinkedInAccount?: boolean;
  selectedLinkedInAccountType?: PostLinkedInAccountType;
  selectedLinkedUrn?: string;
  disableMention?: boolean;
  formId?: string;
  captionEditHistory?: SkedEditorHistory;
  selectedContentLabel?: Label[];
  accountGroupId?: string;
  labelIds?: string[];
}

type CaptionCounterTone = "warning" | "error" | undefined;

export default function CaptionField(props: Props) {
  const {
    platforms = [],
    selectedPlatform,
    value,
    characterCount,
    className,
    label,
    disabled,
    onChange,
    maxLength,
    hashtagCount,
    errors,
    warnings,
    hasMoreThanOnelinkedInAccount,
    selectedLinkedInAccountType,
    selectedLinkedUrn,
    disableMention,
    formId,
    captionEditHistory,
    selectedContentLabel,
    accountGroupId,
    labelIds,
  } = props;
  const editorRef = useRef<LexicalEditor | null>(null);
  const descriptionsLength = useSelector((state: ReduxRootState) =>
    getDescriptions(state, formId ?? null)
  ).length;
  const mentionCount = useMemo(() => {
    const mentionPlatformType =
      selectedPlatform === "LINKED"
        ? platforms.some(({ TYPE }) => TYPE === "FB")
          ? "FB"
          : selectedPlatform
        : selectedPlatform;

    return countMentionsForPlatform({
      value,
      platformType: mentionPlatformType,
    });
  }, [platforms, selectedPlatform, value]);
  const debouncedOnChange = useDebouncedCallback((newValue) => {
    if (value !== newValue) {
      onChange(newValue);
    }
  }, 200);
  // Commit any pending caption edit before a remount cancels the debounce.
  useRegisterFlush(debouncedOnChange.flush);

  const maxAllowedMentionCount = useMemo(() => {
    const hasMultiplePlatforms = platforms.length > 1;

    return hasMultiplePlatforms
      ? getMostRestrictiveMentionLimit(platforms)
      : getPlatformMentionLimit(selectedPlatform);
  }, [platforms, selectedPlatform]);

  const maxHashtagCount = useMemo(() => {
    return getStrictestRuleMax({
      platforms,
      selectedPlatform,
      field: FIELDS.CAPTION,
      rule: RULES.HASHTAG_COUNT,
    });
  }, [platforms, selectedPlatform]);

  const skedCaptionAutocomplete = useSkedCaptionAutocomplete(selectedPlatform);
  const captionSuggestionPlatforms = useMemo(
    () => [
      ...new Set(
        (platforms.length > 0
          ? platforms.map(({ TYPE }) => TYPE)
          : [selectedPlatform]
        ).map((platform) => (platform === "LINKED" ? "LI" : platform))
      ),
    ],
    [platforms, selectedPlatform]
  );
  const characterCounterTone = useMemo<CaptionCounterTone>(() => {
    if (errors?.[FIELDS.CAPTION]?.errors?.length) {
      return "error";
    }

    if (warnings?.[FIELDS.CAPTION]?.warnings?.length) {
      return "warning";
    }

    return;
  }, [errors, warnings]);

  return (
    <div data-testid="postCaption" className={className}>
      <div className="mb-2">
        <PostFieldLabel className="flex items-center">{label}</PostFieldLabel>
      </div>
      <SkedCaptionV2
        className="min-h-[136px]"
        initialValue={value}
        disabled={disabled}
        onChange={debouncedOnChange}
        suggestions={true}
        autocomplete={skedCaptionAutocomplete}
        selectedPlatform={selectedPlatform}
        hasMoreThanOnelinkedInAccount={hasMoreThanOnelinkedInAccount}
        selectedLinkedInAccountType={selectedLinkedInAccountType}
        selectedLinkedUrn={selectedLinkedUrn}
        formId={formId}
        editorHistory={captionEditHistory}
        descriptionsDisabled={descriptionsLength <= 0}
        onEditorContextReady={({ editor }) => {
          editorRef.current = editor;
        }}
        extraActions={
          <InlineAiCaptionPopover
            disabled={disabled}
            currentCaption={value}
            source="caption"
            groupId={accountGroupId}
            labelIds={labelIds}
            selectedContentLabels={selectedContentLabel}
            platforms={captionSuggestionPlatforms}
            formId={formId}
            onUndo={() => {
              // Lexical commands with a void payload still require the argument.
              // eslint-disable-next-line unicorn/no-useless-undefined
              editorRef.current?.dispatchCommand(UNDO_COMMAND, undefined);
            }}
            onApply={(nextCaption, options) => {
              if (!editorRef.current) {
                onChange(nextCaption);
                return;
              }

              editorRef.current.update(
                () => {
                  const root = $getRoot();
                  root.clear();
                  root.append(skedCaptionImport(nextCaption));
                },
                options
                  ? {
                      tag:
                        options.history === "push"
                          ? HISTORY_PUSH_TAG
                          : HISTORY_MERGE_TAG,
                    }
                  : undefined
              );
            }}
          />
        }
        counters={{
          characters:
            typeof characterCount === "number"
              ? characterCount
              : typeof value === "string"
                ? value.length
                : 0,
          charactersMax: maxLength,
          charactersTone: characterCounterTone,
          hashtags: hashtagCount,
          hashtagsMax: maxHashtagCount ?? DEFAULT_HASHTAG_LIMIT,
          mentions:
            !disableMention && mentionCount > 0 ? mentionCount : undefined,
          mentionsMax:
            selectedPlatform !== "LI" && maxAllowedMentionCount
              ? maxAllowedMentionCount
              : 0,
        }}
      />

      <PostFieldError
        className="mt-4"
        fields={[FIELDS.CAPTION]} // todo: move it
        errors={errors}
        warnings={warnings}
      />
    </div>
  );
}
